import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS, useReadCachedListItem } from '@/shared/api';
import type { TableViewSnapshot } from '@/entities/settings/models/types';
import {
  createSharedPreset,
  deleteSharedPreset,
  fetchSharedPresetRecipients,
  fetchSharedPresets,
  pinSharedPreset,
  updateSharedPreset,
} from './api';
import type { SharedPresetListResponse } from './types';

/** Query-ключи совместных пресетов и справочника получателей */
export const sharedPresetKeys = {
  all: [API_TAGS.SHARED_PRESETS] as const,
  lists: () => [...sharedPresetKeys.all, 'list'] as const,
  list: (tableName?: string) => [...sharedPresetKeys.lists(), tableName] as const,
  recipients: (search: string) => [...sharedPresetKeys.all, 'recipients', search] as const,
};

/**
 * Совместные пресеты таблицы или дашбордов, видимые текущему пользователю: и те, которыми он
 * поделился, и те, которыми поделились с ним.
 *
 * Пустое имя таблицы приходит из разделов, где пресетов нет вовсе (выборки): запрос там не
 * нужен, а без имени таблицы сервер отдал бы пресеты всех таблиц разом.
 */
export const useSharedPresets = <TSettings = TableViewSnapshot>(tableName: string) =>
  useQuery({
    queryKey: sharedPresetKeys.list(tableName),
    queryFn: () => fetchSharedPresets<TSettings>(tableName),
    enabled: Boolean(tableName),
  });

/** Чтение совместного пресета из последнего полученного списка в момент вызова ({@link useReadCachedListItem}). */
export const useReadCachedSharedPreset = <TSettings = TableViewSnapshot>(tableName: string) =>
  useReadCachedListItem<SharedPresetListResponse<TSettings>, string>(sharedPresetKeys.list, tableName);

/**
 * Справочник получателей для окна «Поделиться»: активные пользователи, кроме текущего, по запросу
 * из ФИО и логина. Пока ответ на новый запрос не пришёл, остаётся прежняя выдача — список под
 * курсором не пропадает на каждом наборе.
 *
 * @param options.enabled - Запрос только при открытом окне
 */
export const useSharedPresetRecipients = (search: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: sharedPresetKeys.recipients(search),
    queryFn: () => fetchSharedPresetRecipients({ search: search || undefined }),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });

/** Создание совместного пресета; по успеху список перечитывается до колбэков вызывающего */
export const useCreateSharedPreset = <TSettings = TableViewSnapshot>() => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSharedPreset<TSettings>,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sharedPresetKeys.all }),
  });
};

/**
 * Изменение названия, содержимого или получателей совместного пресета владельцем; по успеху
 * список перечитывается до колбэков вызывающего
 */
export const useUpdateSharedPreset = <TSettings = TableViewSnapshot>() => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSharedPreset<TSettings>,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sharedPresetKeys.all }),
  });
};

/** Закрепление совместного пресета в левом меню текущего пользователя */
export const usePinSharedPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pinSharedPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sharedPresetKeys.all }),
  });
};

/** Удаление совместного пресета: владельцем — у всех, получателем — из своего списка */
export const useDeleteSharedPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSharedPreset,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sharedPresetKeys.all }),
  });
};

/**
 * Возврат совместного пресета владельцем в личные: удаление на сервере, после которого вызывающий
 * сам заменяет совместный личным.
 *
 * В отличие от {@link useDeleteSharedPreset}, обещание выполняется по ответу сервера, не дожидаясь
 * перечитывания списков: удалённый в полученном списке ещё есть, и вызывающий в том же такте
 * убирает его ({@link useEvictSharedPreset}) и заводит личный. Ожидание перечитывания — всех
 * запросов совместных пресетов, в том числе справочника получателей — оставило бы промежуток,
 * когда нет ни совместного, ни личного.
 */
export const useUnshareSharedPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSharedPreset,
    onSuccess: () => {
      // Не ожидается: перечитанный список приходит позже, чем вызывающий заменит удалённый личным
      void queryClient.invalidateQueries({ queryKey: sharedPresetKeys.all });
    },
  });
};

/**
 * Убрать совместный пресет из полученных списков, не дожидаясь их перечитывания. Нужно, когда
 * удалённый в том же такте заменяется другим: страница не должна видеть ни промежутка без обоих,
 * ни обоих разом.
 */
export const useEvictSharedPreset = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (id: number) =>
      queryClient.setQueriesData<SharedPresetListResponse<unknown>>(
        { queryKey: sharedPresetKeys.lists() },
        (data) => data && { ...data, items: data.items.filter((preset) => preset.id !== id) },
      ),
    [queryClient],
  );
};
